/**
 * Persona Kütüphanesi — yerel form/kabuk primitifleri.
 *
 * Bu ekranlar `AppShell` altında yaşıyor ve `AdminPage`/`DashboardPage` ile
 * aynı ham-Tailwind dilini konuşuyor (`bg-white/[0.02]`, `border-white/10`,
 * `violet-*` aksan). `components/common/`'daki cam (glass) tasarım sistemi
 * `ConsensusModePage` ağacına ait; ikisini bir sayfada karıştırmak görünür
 * bir kopukluk üretiyor. Bu dosya o ayrımı BİLİNÇLİ olarak sürdürür ve
 * kopyala-yapıştır input'ları tek yerde toplar.
 */
import { useEffect, type ReactNode } from "react";

export function Field({
  label,
  hint,
  children,
  required,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
  required?: boolean;
}) {
  return (
    <label className="block space-y-1.5">
      <span className="flex items-baseline gap-1.5">
        <span className="text-[12px] font-medium text-slate-300">{label}</span>
        {required && <span className="text-[11px] text-rose-400">*</span>}
      </span>
      {children}
      {hint && <span className="block text-[11px] text-slate-500 leading-snug">{hint}</span>}
    </label>
  );
}

const INPUT_CLASS =
  "w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-[13px] text-white " +
  "placeholder:text-slate-600 focus:outline-none focus:border-violet-500/60 " +
  "focus:ring-1 focus:ring-violet-500/30 transition-colors disabled:opacity-50";

export function TextInput(props: React.InputHTMLAttributes<HTMLInputElement>) {
  const { className, ...rest } = props;
  return <input {...rest} className={`${INPUT_CLASS} ${className ?? ""}`} />;
}

export function TextArea(props: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  const { className, ...rest } = props;
  return (
    <textarea
      {...rest}
      className={`${INPUT_CLASS} resize-y leading-relaxed ${className ?? ""}`}
    />
  );
}

export function Select(props: React.SelectHTMLAttributes<HTMLSelectElement>) {
  const { className, children, ...rest } = props;
  return (
    <select {...rest} className={`${INPUT_CLASS} ${className ?? ""}`}>
      {children}
    </select>
  );
}

export function Toggle({
  checked,
  onChange,
  label,
  hint,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
  hint?: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className="flex w-full items-start gap-3 text-left group"
    >
      <span
        className={[
          "mt-0.5 relative h-5 w-9 shrink-0 rounded-full transition-colors duration-200",
          checked ? "bg-violet-500" : "bg-white/15 group-hover:bg-white/25",
        ].join(" ")}
      >
        <span
          className={[
            "absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform duration-200",
            checked ? "translate-x-[18px]" : "translate-x-0.5",
          ].join(" ")}
        />
      </span>
      <span className="min-w-0">
        <span className="block text-[12.5px] text-slate-200">{label}</span>
        {hint && <span className="block text-[11px] text-slate-500 leading-snug">{hint}</span>}
      </span>
    </button>
  );
}

type BtnTone = "primary" | "ghost" | "danger" | "outline";

const BTN_TONE: Record<BtnTone, string> = {
  primary:
    "bg-violet-600 hover:bg-violet-500 text-white border border-violet-500/40 shadow-sm shadow-violet-900/40",
  outline:
    "bg-white/[0.04] hover:bg-white/[0.08] text-slate-200 border border-white/10",
  ghost: "bg-transparent hover:bg-white/[0.06] text-slate-400 hover:text-white border border-transparent",
  danger:
    "bg-rose-600/90 hover:bg-rose-500 text-white border border-rose-500/40",
};

export function Btn({
  tone = "outline",
  size = "md",
  loading,
  children,
  className,
  disabled,
  ...rest
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  tone?: BtnTone;
  size?: "sm" | "md";
  loading?: boolean;
}) {
  const sizing = size === "sm" ? "px-2.5 py-1 text-[11.5px]" : "px-3.5 py-2 text-[12.5px]";
  return (
    <button
      {...rest}
      type={rest.type ?? "button"}
      disabled={disabled || loading}
      className={[
        "inline-flex items-center justify-center gap-1.5 rounded-lg font-medium",
        "transition-all duration-150 disabled:opacity-45 disabled:pointer-events-none",
        sizing,
        BTN_TONE[tone],
        className ?? "",
      ].join(" ")}
    >
      {loading && (
        <span className="h-3 w-3 animate-spin rounded-full border-[1.5px] border-current border-t-transparent" />
      )}
      {children}
    </button>
  );
}

export function Chip({
  children,
  tone = "neutral",
  title,
}: {
  children: ReactNode;
  tone?: "neutral" | "violet" | "green" | "amber" | "rose" | "cyan";
  // `exactOptionalPropertyTypes` açık: çağıranlar `title={x ?? undefined}`
  // gibi ifadeler geçiriyor, bu yüzden `| undefined` AÇIKÇA yazılmalı.
  title?: string | undefined;
}) {
  const tones: Record<string, string> = {
    neutral: "bg-white/[0.05] text-slate-400 border-white/10",
    violet: "bg-violet-500/10 text-violet-300 border-violet-500/25",
    green: "bg-emerald-500/10 text-emerald-300 border-emerald-500/25",
    amber: "bg-amber-500/10 text-amber-300 border-amber-500/25",
    rose: "bg-rose-500/10 text-rose-300 border-rose-500/25",
    cyan: "bg-cyan-500/10 text-cyan-300 border-cyan-500/25",
  };
  return (
    <span
      title={title}
      className={`inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[10.5px] font-medium ${tones[tone]}`}
    >
      {children}
    </span>
  );
}

/**
 * Ofset sayfalayıcı — sunucu tarafı `limit`/`offset` uçları için.
 *
 * Tek bir bileşen, çünkü üç yüzey (Keşif sayfaları, Keşif varlıkları, Kanıt
 * havuzu) aynı sözleşmeyi paylaşıyor ve kopyalanmış sayfa aritmetiği sessizce
 * ayrışır. `total` filtrelenmiş sayı OLMALI; aksi halde son sayfalar boş çıkar.
 *
 * Toplam bir sayfaya sığıyorsa hiç render edilmez: tek sayfalık bir listeye
 * "1 / 1" yazmak gürültüdür.
 */
export function Pager({
  offset,
  limit,
  total,
  onChange,
  busy,
  unit = "kayıt",
}: {
  offset: number;
  limit: number;
  total: number;
  onChange: (nextOffset: number) => void;
  busy?: boolean;
  unit?: string;
}) {
  if (total <= limit) return null;
  const pageCount = Math.ceil(total / limit);
  const page = Math.floor(offset / limit) + 1;
  const from = total === 0 ? 0 : offset + 1;
  const to = Math.min(offset + limit, total);
  return (
    <div className="flex flex-wrap items-center gap-2 border-t border-white/[0.06] pt-2">
      <span className="text-[11px] text-slate-500">
        {from.toLocaleString("tr-TR")}–{to.toLocaleString("tr-TR")} /{" "}
        {total.toLocaleString("tr-TR")} {unit}
      </span>
      <div className="ml-auto flex items-center gap-1.5">
        <Btn
          tone="ghost"
          size="sm"
          disabled={busy || offset <= 0}
          onClick={() => onChange(0)}
          aria-label="İlk sayfa"
          title="İlk sayfa"
        >
          «
        </Btn>
        <Btn
          tone="ghost"
          size="sm"
          disabled={busy || offset <= 0}
          onClick={() => onChange(Math.max(0, offset - limit))}
        >
          ‹ Önceki
        </Btn>
        <span className="px-1 text-[11px] tabular-nums text-slate-400">
          {page} / {pageCount}
        </span>
        <Btn
          tone="ghost"
          size="sm"
          disabled={busy || to >= total}
          onClick={() => onChange(offset + limit)}
        >
          Sonraki ›
        </Btn>
        <Btn
          tone="ghost"
          size="sm"
          disabled={busy || to >= total}
          onClick={() => onChange((pageCount - 1) * limit)}
          aria-label="Son sayfa"
          title="Son sayfa"
        >
          »
        </Btn>
      </div>
    </div>
  );
}

export function ErrorBanner({
  message,
  onDismiss,
}: {
  message: string;
  onDismiss?: () => void;
}) {
  return (
    <div className="flex items-start gap-2 rounded-lg border border-rose-500/25 bg-rose-500/10 px-3 py-2 text-[12px] text-rose-200">
      <span className="mt-px shrink-0">⚠</span>
      <span className="flex-1 leading-relaxed">{message}</span>
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          className="shrink-0 text-rose-300/70 hover:text-rose-200"
          aria-label="Kapat"
        >
          ✕
        </button>
      )}
    </div>
  );
}

export function EmptyState({
  icon,
  title,
  description,
  action,
}: {
  icon?: ReactNode;
  title: string;
  description: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-white/10 bg-white/[0.015] px-6 py-10 text-center">
      {icon && <div className="text-2xl opacity-50">{icon}</div>}
      <div className="text-[13px] font-medium text-slate-300">{title}</div>
      <p className="max-w-md text-[12px] leading-relaxed text-slate-500">{description}</p>
      {action && <div className="mt-2">{action}</div>}
    </div>
  );
}

/**
 * Basit modal kabuğu.
 *
 * Radix `Dialog` yerine düz bir portal-suz overlay kullanılıyor: bu ekranlar
 * `AppShell` içinde tam sayfa render ediliyor, iç içe dialog yok ve Radix'in
 * focus-trap'i uzun formlarda (persona düzenleyici ~15 alan) scroll ile
 * çakışıyor. Escape ve overlay tıklaması elle bağlanır.
 */
export function Modal({
  open,
  onClose,
  title,
  subtitle,
  width = "max-w-3xl",
  children,
  footer,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string | undefined;
  width?: string | undefined;
  children: ReactNode;
  footer?: ReactNode | undefined;
}) {
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    // Arka planın kaymasını engelle — uzun formlarda modal içinde scroll
    // ederken sayfanın da kayması yön kaybına yol açıyor.
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/70 px-4 py-8 backdrop-blur-sm">
      <div
        className="absolute inset-0"
        onClick={onClose}
        aria-hidden
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={`relative w-full ${width} rounded-2xl border border-white/10 bg-[#0d1220] shadow-2xl shadow-black/60`}
      >
        <header className="flex items-start justify-between gap-4 border-b border-white/[0.07] px-5 py-4">
          <div className="min-w-0">
            <h2 className="text-[15px] font-semibold text-white">{title}</h2>
            {subtitle && (
              <p className="mt-0.5 text-[12px] leading-relaxed text-slate-500">{subtitle}</p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-lg p-1.5 text-slate-500 transition-colors hover:bg-white/[0.06] hover:text-white"
            aria-label="Kapat"
          >
            ✕
          </button>
        </header>
        <div className="px-5 py-4">{children}</div>
        {footer && (
          <footer className="flex items-center justify-end gap-2 border-t border-white/[0.07] px-5 py-3">
            {footer}
          </footer>
        )}
      </div>
    </div>
  );
}

export function PersonaAvatar({
  url,
  name,
  color,
  size = 40,
}: {
  url: string | null;
  name: string;
  color: string;
  size?: number;
}) {
  const initials = name
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w.charAt(0))
    .join("")
    .toLocaleUpperCase("tr-TR");
  return (
    <span
      className="inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full border border-white/15 bg-white/[0.06] font-semibold text-white"
      style={{
        width: size,
        height: size,
        fontSize: size * 0.34,
        boxShadow: `0 0 0 2px ${color}33`,
      }}
    >
      {url ? (
        <img src={url} alt="" className="h-full w-full object-cover" loading="lazy" />
      ) : (
        initials || "?"
      )}
    </span>
  );
}

export function SectionTitle({
  title,
  hint,
  action,
}: {
  title: string;
  hint?: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div className="min-w-0">
        <h3 className="text-[13px] font-semibold text-slate-200">{title}</h3>
        {hint && <p className="mt-0.5 text-[11.5px] leading-relaxed text-slate-500">{hint}</p>}
      </div>
      {action}
    </div>
  );
}
